import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, Thumb } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { DiscountBox } from "./DiscountBox";
import { discountCalc } from "./discount";

const PAYMENT_METHODS = ["Na retirada", "PIX", "Dinheiro", "Cartão"];
const DELIVERY = ["Retirada", "Entrega"];

const emptyItem = { ref: "", variation_id: "", quantity: "1", unit_price: "" };

export function NewOrderModal({ onClose, onCreated, customer }) {
  const [products, setProducts] = useState([]);
  const [kits, setKits] = useState([]);
  const [name, setName] = useState(customer?.name || "");
  const [phone, setPhone] = useState(customer?.phone || "");
  const [notes, setNotes] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("AGUARDANDO");
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0]);
  const [delivery, setDelivery] = useState(DELIVERY[0]);
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [discountType, setDiscountType] = useState("PERCENTUAL");
  const [discountValue, setDiscountValue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/kits")])
      .then(([p, k]) => {
        setProducts(p.data.filter((x) => x.active !== false));
        setKits(k.data.filter((x) => x.active !== false));
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  // "PRODUTO — nome" / "KIT — nome". A referência guarda o tipo junto com o id.
  const options = [
    ...products.map((p) => ({ value: `product:${p.id}`, label: `PRODUTO — ${p.name}` })),
    ...kits.map((k) => ({ value: `kit:${k.id}`, label: `KIT — ${k.name}` })),
  ];

  const resolve = (ref) => {
    if (!ref) return null;
    const [kind, id] = ref.split(":");
    if (kind === "kit") {
      const kit = kits.find((k) => k.id === id);
      return kit
        ? { kind, entity: kit, variations: kit.available_variations || [] }
        : null;
    }
    const product = products.find((p) => p.id === id);
    return product
      ? {
          kind,
          entity: product,
          variations: (product.variations || []).filter((v) => v.active !== false),
        }
      : null;
  };

  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const priceOf = (it) => {
    const found = resolve(it.ref);
    if (!found) return 0;
    return it.unit_price !== "" ? Number(it.unit_price) : Number(found.entity.price || 0);
  };

  const total = useMemo(
    () => items.reduce((s, it) => s + priceOf(it) * (Number(it.quantity) || 0), 0),
    [items, products, kits]
  );

  const submit = async (e) => {
    e.preventDefault();
    const rows = items.filter((it) => it.ref);
    if (!rows.length) {
      toast.error("Adicione pelo menos um produto ou kit ao pedido.");
      return;
    }
    for (const it of rows) {
      const found = resolve(it.ref);
      if (found?.variations.length > 0 && !it.variation_id) {
        toast.error(`Escolha a combinação de ${found.entity.name}.`);
        return;
      }
    }
    const calc = discountCalc(total, discountType, discountValue);
    if (calc.error) {
      toast.error(calc.error);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        discount_type: Number(discountValue) > 0 ? discountType : null,
        discount_value: Number(discountValue) || 0,
        customer_name: name.trim(),
        customer_id: customer?.id || null,
        phone: phone.trim(),
        notes: notes.trim(),
        payment_status: paymentStatus,
        payment_method: paymentMethod,
        delivery,
        items: rows.map((it) => {
          const [kind, id] = it.ref.split(":");
          return {
            product_id: kind === "product" ? id : null,
            kit_id: kind === "kit" ? id : null,
            variation_id: it.variation_id || null,
            quantity: Number(it.quantity) || 1,
            unit_price: it.unit_price === "" ? null : Number(it.unit_price),
          };
        }),
      });
      onCreated(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Novo pedido"
      description="Para vendas combinadas no WhatsApp ou no Instagram. O pedido é igual ao do catálogo e pode ficar com pagamento em aberto."
      testid="new-order-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome do cliente">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Cris"
              required
              minLength={2}
              data-testid="new-order-name"
            />
          </Field>
          <Field label="WhatsApp">
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(00) 00000-0000"
              required
              minLength={8}
              className="font-num"
              data-testid="new-order-phone"
            />
          </Field>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Itens do pedido
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setItems((prev) => [...prev, { ...emptyItem }])}
              data-testid="new-order-add-item"
              className="h-8 gap-1.5 rounded-full text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> Adicionar item
            </Button>
          </div>
          {items.map((it, idx) => {
            const found = resolve(it.ref);
            const variations = found?.variations || [];
            const variation = variations.find((v) => v.id === it.variation_id);
            const photo = variation?.photo_path || found?.entity?.photo_path;
            return (
              <div
                key={idx}
                className="space-y-3 rounded-lg border border-white/10 bg-secondary/40 p-3"
                data-testid={`new-order-item-${idx}`}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <NativeSelect
                      value={it.ref}
                      onChange={(v) => setItem(idx, { ref: v, variation_id: "" })}
                      placeholder="Escolha o produto ou kit"
                      options={options}
                      data-testid={`new-order-product-${idx}`}
                    />
                  </div>
                  {items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                      data-testid={`new-order-remove-item-${idx}`}
                      className="h-9 px-2 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {found && (
                  <div className="flex items-center gap-2">
                    {photo && (
                      <Thumb src={fileUrl(photo)} alt={found.entity.name} className="h-9 w-9" />
                    )}
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        found.kind === "kit"
                          ? "border-brand/40 bg-brand/15 text-brand"
                          : "border-white/15 bg-secondary/60 text-muted-foreground"
                      }`}
                      data-testid={`new-order-kind-${idx}`}
                    >
                      {found.kind === "kit" ? "Kit" : "Produto"}
                    </span>
                    <span className="min-w-0 truncate text-xs text-muted-foreground">
                      {found.entity.name}
                      {variation ? ` · ${variation.name}` : ""}
                    </span>
                  </div>
                )}
                {variations.length > 0 && (
                  <NativeSelect
                    value={it.variation_id}
                    onChange={(v) => setItem(idx, { variation_id: v })}
                    placeholder="Escolha a combinação"
                    options={variations.map((v) => ({ value: v.id, label: v.name }))}
                    data-testid={`new-order-variation-${idx}`}
                  />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Quantidade">
                    <Input
                      type="number"
                      min="1"
                      value={it.quantity}
                      onChange={(e) => setItem(idx, { quantity: e.target.value })}
                      className="font-num"
                      data-testid={`new-order-quantity-${idx}`}
                    />
                  </Field>
                  <Field
                    label="Preço unitário"
                    hint={found ? `Catálogo: ${brl(found.entity.price || 0)}` : "Opcional"}
                  >
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={it.unit_price}
                      onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                      placeholder={found ? String(found.entity.price ?? "") : ""}
                      className="font-num"
                      data-testid={`new-order-price-${idx}`}
                    />
                  </Field>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Pagamento">
            <NativeSelect
              value={paymentStatus}
              onChange={setPaymentStatus}
              options={[
                { value: "AGUARDANDO", label: "Aguardando pagamento" },
                { value: "CONFIRMADO", label: "Já pago" },
              ]}
              data-testid="new-order-payment-status"
            />
          </Field>
          <Field label="Forma / previsão">
            <NativeSelect
              value={paymentMethod}
              onChange={setPaymentMethod}
              options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
              data-testid="new-order-payment-method"
            />
          </Field>
          <Field label="Entrega">
            <NativeSelect
              value={delivery}
              onChange={setDelivery}
              options={DELIVERY.map((d) => ({ value: d, label: d }))}
              data-testid="new-order-delivery"
            />
          </Field>
        </div>

        <Field label="Observações">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Combinado no WhatsApp, retira sábado..."
            data-testid="new-order-notes"
          />
        </Field>

        <DiscountBox
          subtotal={total}
          type={discountType}
          value={discountValue}
          onType={setDiscountType}
          onValue={setDiscountValue}
          testid="new-order"
        />

        {paymentStatus === "CONFIRMADO" && (
          <p className="text-xs text-amber-300">
            Ao criar com pagamento confirmado o estoque é baixado e a receita é lançada, igual ao
            fluxo do catálogo.
          </p>
        )}

        <Button
          type="submit"
          disabled={busy}
          className="h-11 gap-2 rounded-full font-semibold"
          data-testid="new-order-submit"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Criar pedido
        </Button>
      </form>
    </ResponsiveModal>
  );
}
